"use client";

import { StateCreator } from "zustand";

export type TechniqueTab = "hka" | "balance" | "distal" | "rom";

export interface TechniqueHkaClickFilter {
  change: number;
  isCrossover: boolean;
}

export const TechniqueTabs: { key: string; label: string }[] = [
  { key: "hka", label: "HKA" },
  { key: "balance", label: "Gaps & Laxity" },
  { key: "distal", label: "Bone Cuts & Implant Positioning" },
  { key: "rom", label: "Intra-Op ROM" },
];

export const HKA_VISIBLE_SERIES_ALL = ["Initial HKA", "Final HKA"];

export const BALANCE_GAPS_ALL = ["Lateral", "Medial"];
export const BALANCE_LAXITY_ALL = ["Final Laxity", "Initial Laxity"];

export const ROM_EXT_SERIES_ALL = [
  "Change in Max Extension Angle",
  "Final Max Extension Angle",
  "Initial Max Extension Angle",
];
export const ROM_FLEX_SERIES_ALL = [
  "Change in Max Flexion Angle",
  "Final Max Flexion Angle",
  "Initial Max Flexion Angle",
];

export const HKA_CROSSOVER_ALL = ["Crossover", "No Crossover", ""];
export const HKA_CLASS_ALL = ["Varus", "Valgus", "Neutral", ""];
export const CROSSOVER_DIR_ALL = ["Valgus to Varus", "Varus to Valgus", ""];
export const CHANGE_HKA_ALL = ["<=3", ">3-<=5", ">5-<=7", ">7", ""];
export const EXT_ANGLE_ALL = ["Flexion Contracture", "Hyperextension", ""];

export type TechniqueAnalyticsState = {
  techniqueTab: TechniqueTab;
  setTechniqueTab: (tab: TechniqueTab) => void;

  // HKA tab
  techniqueHkaDateFrom: string;
  techniqueHkaDateTo: string;
  techniqueHkaCrossover: string[];
  techniqueHkaClickFilter: TechniqueHkaClickFilter | null;
  techniqueHkaVisibleSeries: string[];

  setTechniqueHkaDateFrom: (date: string) => void;
  setTechniqueHkaDateTo: (date: string) => void;
  setTechniqueHkaCrossover: (v: string[]) => void;
  setTechniqueHkaClickFilter: (f: TechniqueHkaClickFilter | null) => void;
  setTechniqueHkaVisibleSeries: (series: string[]) => void;

  // Balance tab
  techniqueBalanceInitialHka: string[];
  techniqueBalanceCrossover: string[];
  techniqueBalanceChangeHka: string[];
  techniqueBalanceExtAngle: string[];
  techniqueBalanceGapsVisible: string[];
  techniqueBalanceMlLaxityVisible: string[];
  techniqueBalanceFeLaxityVisible: string[];

  setTechniqueBalanceInitialHka: (v: string[]) => void;
  setTechniqueBalanceCrossover: (v: string[]) => void;
  setTechniqueBalanceChangeHka: (v: string[]) => void;
  setTechniqueBalanceExtAngle: (v: string[]) => void;
  setTechniqueBalanceGapsVisible: (series: string[]) => void;
  setTechniqueBalanceMlLaxityVisible: (series: string[]) => void;
  setTechniqueBalanceFeLaxityVisible: (series: string[]) => void;

  // Distal tab
  techniqueDistalInitialHka: string[];
  setTechniqueDistalInitialHka: (v: string[]) => void;

  // ROM tab
  techniqueRomExtAngle: string[];
  techniqueRomExtVisible: string[];
  techniqueRomFlexVisible: string[];

  setTechniqueRomExtAngle: (v: string[]) => void;
  setTechniqueRomExtVisible: (series: string[]) => void;
  setTechniqueRomFlexVisible: (series: string[]) => void;

  resetTechniqueFilters: () => void;
};

export const createTechniqueAnalyticsSlice: StateCreator<
  TechniqueAnalyticsState
> = (set) => ({
  techniqueTab: "hka",
  setTechniqueTab: (tab) => set({ techniqueTab: tab }),

  // HKA
  techniqueHkaDateFrom: "",
  techniqueHkaDateTo: "",
  techniqueHkaCrossover: [],
  techniqueHkaClickFilter: null,
  techniqueHkaVisibleSeries: [...HKA_VISIBLE_SERIES_ALL],

  setTechniqueHkaDateFrom: (date) => set({ techniqueHkaDateFrom: date }),
  setTechniqueHkaDateTo: (date) => set({ techniqueHkaDateTo: date }),
  setTechniqueHkaCrossover: (v) => set({ techniqueHkaCrossover: v }),
  setTechniqueHkaClickFilter: (f) => set({ techniqueHkaClickFilter: f }),
  setTechniqueHkaVisibleSeries: (series) =>
    set({ techniqueHkaVisibleSeries: series }),

  // Balance
  techniqueBalanceInitialHka: [],
  techniqueBalanceCrossover: [],
  techniqueBalanceChangeHka: [],
  techniqueBalanceExtAngle: [],
  techniqueBalanceGapsVisible: [...BALANCE_GAPS_ALL],
  techniqueBalanceMlLaxityVisible: [...BALANCE_LAXITY_ALL],
  techniqueBalanceFeLaxityVisible: [...BALANCE_LAXITY_ALL],

  setTechniqueBalanceInitialHka: (v) => set({ techniqueBalanceInitialHka: v }),
  setTechniqueBalanceCrossover: (v) => set({ techniqueBalanceCrossover: v }),
  setTechniqueBalanceChangeHka: (v) => set({ techniqueBalanceChangeHka: v }),
  setTechniqueBalanceExtAngle: (v) => set({ techniqueBalanceExtAngle: v }),
  setTechniqueBalanceGapsVisible: (series) =>
    set({ techniqueBalanceGapsVisible: series }),
  setTechniqueBalanceMlLaxityVisible: (series) =>
    set({ techniqueBalanceMlLaxityVisible: series }),
  setTechniqueBalanceFeLaxityVisible: (series) =>
    set({ techniqueBalanceFeLaxityVisible: series }),

  // Distal
  techniqueDistalInitialHka: [],
  setTechniqueDistalInitialHka: (v) => set({ techniqueDistalInitialHka: v }),

  // ROM
  techniqueRomExtAngle: [],
  techniqueRomExtVisible: [...ROM_EXT_SERIES_ALL],
  techniqueRomFlexVisible: [...ROM_FLEX_SERIES_ALL],

  setTechniqueRomExtAngle: (v) => set({ techniqueRomExtAngle: v }),
  setTechniqueRomExtVisible: (series) =>
    set({ techniqueRomExtVisible: series }),
  setTechniqueRomFlexVisible: (series) =>
    set({ techniqueRomFlexVisible: series }),

  resetTechniqueFilters: () =>
    set((state) => {
      switch (state.techniqueTab) {
        case "hka":
          return {
            techniqueHkaDateFrom: "",
            techniqueHkaDateTo: "",
            techniqueHkaCrossover: [],
            techniqueHkaClickFilter: null,
            techniqueHkaVisibleSeries: [...HKA_VISIBLE_SERIES_ALL],
          };
        case "balance":
          return {
            techniqueBalanceInitialHka: [],
            techniqueBalanceCrossover: [],
            techniqueBalanceChangeHka: [],
            techniqueBalanceExtAngle: [],
            techniqueBalanceGapsVisible: [...BALANCE_GAPS_ALL],
            techniqueBalanceMlLaxityVisible: [...BALANCE_LAXITY_ALL],
            techniqueBalanceFeLaxityVisible: [...BALANCE_LAXITY_ALL],
          };
        case "distal":
          return { techniqueDistalInitialHka: [] };
        case "rom":
          return {
            techniqueRomExtAngle: [],
            techniqueRomExtVisible: [...ROM_EXT_SERIES_ALL],
            techniqueRomFlexVisible: [...ROM_FLEX_SERIES_ALL],
          };
      }
    }),
});
